import { useState, useEffect, useRef, useCallback } from 'react';

interface UseCountdownOptions {
  initialTime: number;
  onComplete?: () => void;
  onTick?: (timeLeft: number) => void;
  autoStart?: boolean;
}

export const useCountdown = ({
  initialTime,
  onComplete,
  onTick,
  autoStart = false
}: UseCountdownOptions) => {
  const [timeLeft, setTimeLeft] = useState(initialTime);
  const [isRunning, setIsRunning] = useState(autoStart);
  const [isComplete, setIsComplete] = useState(false);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const onCompleteRef = useRef(onComplete);
  const onTickRef = useRef(onTick);

  // Update refs to avoid stale closures
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onTickRef.current = onTick;
  }, [onComplete, onTick]);

  const start = useCallback(() => {
    if (timeLeft <= 0) {
      return;
    }
    setIsRunning(true);
  }, [timeLeft]);

  const pause = useCallback(() => {
    setIsRunning(false);
  }, []);

  const reset = useCallback((newTime?: number) => {
    const resetTime = newTime ?? initialTime;
    setTimeLeft(resetTime);
    setIsComplete(false);
    setIsRunning(false);
  }, [initialTime]);

  const stop = useCallback(() => {
    setIsRunning(false);
    setTimeLeft(initialTime);
    setIsComplete(false);
  }, [initialTime]);

  // Main countdown effect
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!isRunning || timeLeft <= 0) {
      return;
    }

    // Create interval
    intervalRef.current = setInterval(() => {
      setTimeLeft(prevTime => {
        const newTime = prevTime - 1;
        
        // Call onTick callback
        if (onTickRef.current) {
          onTickRef.current(newTime);
        }

        // Check if countdown is complete
        if (newTime <= 0) {
          setIsRunning(false);
          setIsComplete(true);
          
          // Call onComplete callback
          if (onCompleteRef.current) {
            onCompleteRef.current();
          }
          
          return 0;
        }

        return newTime;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRunning, timeLeft]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const progress = initialTime > 0 ? ((initialTime - timeLeft) / initialTime) * 100 : 0;

  return {
    timeLeft,
    isRunning,
    isComplete,
    progress,
    start,
    pause,
    reset,
    stop
  };
};